/**
 * Formats dates according to regional locale preferences
 * Replaces hardcoded date formatting across the application
 * Supports: en (US), es (Spanish), fr (French), pt (Portuguese)
 */

import {format} from 'date-fns';
import {getDateFnsLocale, getDateFormatPattern} from './dateLocaleMapper';

type LocaleCode = 'en' | 'es' | 'fr' | 'pt';

/**
 * Formats a date using i18n language preferences
 * @param date - Date to format (Date object or timestamp)
 * @param language - Language code (en, es, fr, pt)
 * @param customPattern - Optional custom format pattern. If not provided, uses default for language
 * @returns Formatted date string
 *
 * @example
 * // US format: 12/25/2026
 * formatDateByLocale(new Date('2026-12-25'), 'en');
 *
 * // Spanish format: 25/12/2026
 * formatDateByLocale(new Date('2026-12-25'), 'es');
 *
 * // Custom format: "December 25, 2026"
 * formatDateByLocale(new Date('2026-12-25'), 'en', 'MMMM dd, yyyy');
 */
export const formatDateByLocale = (
  date: Date | number | string | null | undefined,
  language: LocaleCode = 'en',
  customPattern?: string
): string => {
  // Handle null/undefined
  if (date === null || date === undefined) {
    return '';
  }

  try {
    const dateObj =
      typeof date === 'string'
        ? /^\d{4}-\d{2}-\d{2}$/.test(date)
          ? new Date(date + 'T00:00:00')
          : new Date(date)
        : typeof date === 'number'
        ? new Date(date)
        : date;

    // Check if date is invalid
    if (isNaN(dateObj.getTime())) {
      return '';
    }
    const dateLocale = getDateFnsLocale(language);
    const pattern = customPattern || getDateFormatPattern(language);

    return format(dateObj, pattern, {locale: dateLocale});
  } catch (error) {
    console.error(`Error formatting date with language "${language}":`, error);
    return '';
  }
};

/**
 * Formats a date range (from...to)
 * Useful for displaying leave/absence periods
 * @param startDate - Start date
 * @param endDate - End date
 * @param language - Language code (en, es, fr, pt)
 * @returns Formatted range string (e.g., "Dec 25 - Dec 31, 2026")
 *
 * @example
 * formatDateRangeByLocale(
 *   new Date('2026-12-25'),
 *   new Date('2026-12-31'),
 *   'en'
 * ); // "Dec 25 - Dec 31, 2026"
 */
export const formatDateRangeByLocale = (
  startDate: Date | number | string,
  endDate: Date | number | string,
  language: LocaleCode = 'en'
): string => {
  try {
    const start = formatDateByLocale(startDate, language);
    const end = formatDateByLocale(endDate, language);
    return `${start} - ${end}`;
  } catch (error) {
    console.error('Error formatting date range:', error);
    return 'Invalid date range';
  }
};

/**
 * Formats a date relative to now (e.g., "2 days ago", "in 3 days")
 * Useful for timestamps and status updates
 * @param date - Date to format
 * @param language - Language code (en, es, fr, pt)
 * @returns Relative time string
 *
 * @example
 * formatRelativeDate(new Date(Date.now() - 2*24*60*60*1000), 'en'); // "2 days ago"
 */
export const formatRelativeDate = (
  date: Date | number | string,
  language: LocaleCode = 'en'
): string => {
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const now = new Date();
    const diffMs = now.getTime() - new Date(dateObj).getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    const translations = {
      en: {
        today: 'today',
        yesterday: 'yesterday',
        daysAgo: 'days ago',
        tomorrow: 'tomorrow',
        inDays: 'in',
        days: 'days',
      },
      es: {
        today: 'hoy',
        yesterday: 'ayer',
        daysAgo: 'hace',
        tomorrow: 'mañana',
        inDays: 'en',
        days: 'días',
      },
      fr: {
        today: "aujourd'hui",
        yesterday: 'hier',
        daysAgo: 'il y a',
        tomorrow: 'demain',
        inDays: 'dans',
        days: 'jours',
      },
      pt: {
        today: 'hoje',
        yesterday: 'ontem',
        daysAgo: 'há',
        tomorrow: 'amanhã',
        inDays: 'em',
        days: 'dias',
      },
    };

    const t = translations[language];

    if (diffDays === 0) return t.today;
    if (diffDays === 1) return t.yesterday;
    if (diffDays < 0 && diffDays === -1) return t.tomorrow;
    if (diffDays < 0) return `${t.inDays} ${Math.abs(diffDays)} ${t.days}`;
    return `${t.daysAgo} ${diffDays} ${t.days}`;
  } catch (error) {
    console.error('Error formatting relative date:', error);
    return formatDateByLocale(date, language);
  }
};

/**
 * LEGACY FUNCTIONS (kept for backwards compatibility)
 * These functions are hardcoded to Spanish and should be migrated to use formatDateByLocale
 */

import {UpperCaseFirstLetter} from './upperCaseFirstLetter';

/*format : 31 MARZO*/
export const buildDate = (date: string) => {
  const parse = parseInt(date);
  const newFormat = new Date(parse);

  const moth = new Intl.DateTimeFormat('es', {
    month: 'long',
  }).format(newFormat);

  const day = new Intl.DateTimeFormat('es', {
    day: 'numeric',
  }).format(newFormat);

  return `${day} ${moth.toUpperCase()}`;
};

/*format : Jueves, 31 de Marzo*/
export const buildDateLarge = (date: number) => {
  const newFormat = new Date(date);

  const anio = new Intl.DateTimeFormat('es-MX', {
    year: 'numeric',
  }).format(newFormat);

  const moth = new Intl.DateTimeFormat('es-MX', {
    month: 'long',
  }).format(newFormat);

  const day = new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
  }).format(newFormat);

  const dayName = new Intl.DateTimeFormat('es-MX', {
    weekday: 'long',
  }).format(newFormat);

  return `${UpperCaseFirstLetter(dayName)}, ${day} de ${UpperCaseFirstLetter(
    moth
  )} del ${anio}`;
};

/*format : 1 ENERO - 31 MARZO*/
export const formatDates = (dateini: string, datefin: string) => {
  const dayIni = buildDate(dateini);
  const dayFin = buildDate(datefin);
  return `${dayIni} - ${dayFin}`;
};

/*format : dd/mm/yyyy*/
export const buildDateFormat = (date: string) => {
  const parse = parseInt(date);
  const newFormat = new Date(parse);

  const moth = new Intl.DateTimeFormat('es-MX').format(newFormat);

  return `${moth.toUpperCase()}`;
};

/*returns difference of days between two dates */
export const expirationDays = (date: string) => {
  const dayLimit = parseInt(date);

  const dayNow = new Date().getTime();

  const diff = dayLimit - dayNow;

  const formatt = Math.round(diff / (1000 * 60 * 60 * 24));

  return formatt;
};

export const formatGetTime = (date: string) => {
  return new Date(date).getTime();
};

/*return function getTime() without hours */
export const formatGetTimeCeroDay = () => {
  const datef = new Date();
  datef.setHours(0, 0, 0, 0);
  return datef.getTime();
};
